import { ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useAiStreaming } from '@/composables/yagiprime/useAiStreaming'
import { emitter } from '@/plugins/emitter'
import type { ShokurekishoFormatData } from '@/composables/format-edit/useShokurekishoFormatData'

type AiAxiosVm = {
  $axios: {
    post: (
      url: string,
      data: FormData
    ) => Promise<{
      data: { success?: boolean; usage_count?: number }
    }>
  }
  $yagiprimePaymentService: {
    useFeature: (
      vm: unknown,
      feature: string,
      hooks: { onSuccess: () => void; onCancel?: () => void }
    ) => void
  }
}

type GlobalVarWithPayments = {
  isYagiPrime: boolean
  in_app_payments?: boolean
}

export function useShokurekishoAiSummary(formatData: ShokurekishoFormatData) {
  const { vm, varuna, site, deviceLib, globalVar, yagiprimePaymentService } =
    useHostServices()
  const host = vm as unknown as AiAxiosVm
  const { user, isAuthenticated } = useAuth()

  const aiSummaryVisible = ref(false)
  const aiSummaryResultView = ref(false)
  const aiResultSummaryResult = ref('')
  const aiUsageCount = ref(0)
  const aiMaxUsageCount = ref(1)

  const {
    aiIsStreaming,
    aiIsThinking,
    aiThinkingLabel,
    aiReasoningText,
    aiShowReasoning,
    startAIStream,
    toggleAiReasoning,
    cancelAIStream
  } = useAiStreaming()

  const gVar = globalVar as typeof globalVar & GlobalVarWithPayments

  function fetchAiUsageCount() {
    if (!isAuthenticated.value || !user.value?.uid) {
      return Promise.resolve()
    }
    const body = new FormData()
    body.append('uuid', user.value.uid)
    body.append('category_type', 'summary')
    return host.$axios
      .post(varuna.endPoints.getAiUsageCount, body)
      .then((response) => {
        if (response.data.success) {
          aiUsageCount.value = response.data.usage_count || 0
          aiMaxUsageCount.value = gVar.isYagiPrime ? 10 : 1
        }
      })
  }

  function openAiSummaryDialog() {
    fetchAiUsageCount().finally(() => {
      aiSummaryVisible.value = true
    })
  }

  function closeAiSummaryDialog() {
    cancelAIStream()
    aiSummaryVisible.value = false
  }

  function getAISummary() {
    if (!gVar.isYagiPrime && aiUsageCount.value >= 1) {
      if (formatData.jobSummary) {
        aiResultSummaryResult.value = formatData.jobSummary.replace(
          /\n/g,
          '<br>'
        )
        aiSummaryResultView.value = true
        return
      }
    }
    const body = new FormData()
    if (isAuthenticated.value && user.value?.uid) {
      body.append('uuid', user.value.uid)
    }
    body.append(
      'data',
      JSON.stringify(formatData.skillsToUse) +
        JSON.stringify(formatData.jobHistories)
    )
    body.append('app_flag', site.appFlag)
    body.append('device_flg', deviceLib.isMobileDevice ? 'SP' : 'PC')
    body.append('version', '3')
    body.append('site_flag', 'r')

    startAIStream(varuna.endPoints.aiSummary, body, {
      resultRef: aiResultSummaryResult,
      viewRef: aiSummaryResultView,
      onError: (error) => {
        const message = error.message || '失敗しました'
        if (message === 'AIの使用回数が上限に達しました。') {
          emitter.emit('show-yagiprime-payment-dialog', 'self-summary-shokumu')
        }
      }
    })
  }

  function getAISummaryContinue() {
    const body = new FormData()
    if (isAuthenticated.value && user.value?.uid) {
      body.append('uuid', user.value.uid)
    }
    body.append(
      'data',
      '作成文章:職務要約\n' +
        JSON.stringify(formatData.skillsToUse) +
        JSON.stringify(formatData.jobHistories) +
        aiResultSummaryResult.value.replace(/<br>/gi, '\n')
    )
    body.append('app_flag', site.appFlag)
    body.append('device_flg', deviceLib.isMobileDevice ? 'SP' : 'PC')
    body.append('version', '3')
    body.append('site_flag', 'r')
    startAIStream(varuna.endPoints.aiSentenseContinue, body, {
      resultRef: aiResultSummaryResult,
      viewRef: aiSummaryResultView
    })
  }

  function goYagiprimeRegisterSummary() {
    yagiprimePaymentService.useFeature(vm, 'self-summary-shokumu', {
      onSuccess: () => {
        getAISummaryContinue()
      },
      onCancel: () => {}
    })
  }

  function applySummaryResult() {
    if (aiIsStreaming.value) return
    formatData.jobSummary = aiResultSummaryResult.value.replace(/<br>/gi, '\n')
    closeAiSummaryDialog()
    aiSummaryResultView.value = false
  }

  return {
    aiSummaryVisible,
    aiSummaryResultView,
    aiResultSummaryResult,
    aiUsageCount,
    aiMaxUsageCount,
    aiIsStreaming,
    aiIsThinking,
    aiThinkingLabel,
    aiReasoningText,
    aiShowReasoning,
    openAiSummaryDialog,
    closeAiSummaryDialog,
    getAISummary,
    goYagiprimeRegisterSummary,
    applySummaryResult,
    toggleAiReasoning,
    cancelAIStream,
    inAppPayments: () => !!gVar.in_app_payments,
    isYagiPrime: () => !!gVar.isYagiPrime
  }
}
